import React from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import EditDeliveryAddressFields from '../EditDeliveryAddressField/EditDeliveryAddressField';
import Card from 'components/Account/Common/Card/Card';
import Button from 'components/Common/Button/Button';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import OptionNotPossibleConditions from 'components/Common/TermConditions/OptionNotPossibleConditions';
import { FLASH_MESSAGE_STATE_KEY } from 'contexts/flashMessageContext';
import { useCustomer } from 'hooks/useCustomer';
import useCurrentCountry from 'hooks/useCurrentCountry';
import { validationSchemaSecondary } from 'schemas/AccountEditDeliveryAddressErrors';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { getRoutesUri } from 'services/generic';

import styles from './EditSecondaryAddress.module.scss';

function EditSecondaryAddress(): JSX.Element {
  const { t } = useTranslation(['deliveryAddressPage']);
  const navigate = useNavigate();
  const { customer } = useCustomer();
  const { isUk } = useCurrentCountry();
  const { state } = useLocation();
  const { S: secondaryAddress } = customer?.addresses ?? {};
  const isSecondaryExist = !!secondaryAddress?.street;
  const title = secondaryAddress
    ? 'LABEL_HEADER_EDIT_SECONDARY_ADDRESS'
    : 'LABEL_OPTIONAL_ADDRESS_CREATE_ADDRESS';

  const labelButton = secondaryAddress
    ? 'LABEL_UPDATE_BUTTON'
    : 'LABEL_CREATE_BUTTON';

  const initialValues = {
    street: secondaryAddress?.street ?? '',
    nameAddress: secondaryAddress?.nameAddress ?? '',
    nameComplement: secondaryAddress?.nameComplement ?? '',
    saidPlace: secondaryAddress?.saidPlace ?? '',
    postalCode: secondaryAddress?.postalCode ?? '',
    city: customer?.addresses.S?.city ?? '',
    country: isUk
      ? t('LABEL_UNITED_KINGDOM', { ns: 'translation' })
      : t('LABEL_FRANCE', { ns: 'translation' }),
  };

  const onSubmit = (values: typeof initialValues) => {
    const body = {
      nameAddress: values.nameAddress,
      nameComplement: values.nameComplement,
      street: values.street,
      lieudit: values.saidPlace,
      postalCode: values.postalCode,
      locality: values.city,
      nom: '',
    };

    fetcherWithBody(apiUrls.getCustomerChangeSecondaryAddress(), body).then(
      () => {
        if (state?.redirectShippingPage) {
          navigate(getRoutesUri<string>('checkoutShipping'));
        } else {
          navigate(getRoutesUri<string>('customerAccountInformation'), {
            state: {
              [FLASH_MESSAGE_STATE_KEY]: {
                message: isSecondaryExist
                  ? t('LABEL_EDIT_OPTIONAL_ADDRESS_SUCCESS')
                  : t('LABEL_CREATE_OPTIONAL_ADDRESS_SUCCESS'),
                severity: 'success',
              },
            },
          });
        }
      }
    );
  };

  return (
    <Card classes={{ container: styles.border }}>
      <div className={styles.title}>
        <label>
          {t(title)}
          {isUk && <span>**</span>}
        </label>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemaSecondary(isUk, t)}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        <Form noValidate>
          <FormikErrorsGtmLogger
            page_type="compte"
            form_type="mon adresse optionnelle"
          />
          <div className={styles.divContainer}>
            <EditDeliveryAddressFields />
          </div>
          <div className={styles.actionToolbar}>
            <Button>{t(labelButton)}</Button>
            <div className={styles.requiredField}>
              <label>
                <span>*</span>
                {t('LABEL_REQUIRED_INFORMATION', { ns: 'translation' })}
              </label>
              {isUk && <OptionNotPossibleConditions asteriskPrefix={'**'} />}
            </div>
          </div>
          <ScrollToFieldError />
        </Form>
      </Formik>
    </Card>
  );
}

export default EditSecondaryAddress;
